import { useEffect, useMemo, useState, useCallback } from "react";
import { api, FUNCTIONS, PRIORITIES, TASK_STATUSES } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PriorityBadge, StatusBadge } from "../components/Badges";
import { Download, Plus, ListChecks } from "lucide-react";
import { toast } from "sonner";
import Attachments from "../components/Attachments";
import Comments from "../components/Comments";

const empty = { title: "", function: "sales", project_id: "", owner_id: "", due_date: "",
  priority: "medium", status: "not_started", notes: "", dependencies: [], subtasks: [], recurring: "" };

function toCSV(rows) {
  if (!rows.length) return "";
  const keys = Object.keys(rows[0]);
  return [keys.join(","), ...rows.map((r) => keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
}

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ status: "", function: "", owner_id: "", priority: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/tasks", { params }).then((r) => setTasks(r.data));
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return tasks
      .filter((t) => !q || t.title?.toLowerCase().includes(s))
      .sort((a, b) => {
        const po = { critical: 0, high: 1, medium: 2, low: 3 };
        if ((a.status === "completed") !== (b.status === "completed")) return a.status === "completed" ? 1 : -1;
        if (po[a.priority] !== po[b.priority]) return (po[a.priority] ?? 4) - (po[b.priority] ?? 4);
        return (a.due_date || "9999").localeCompare(b.due_date || "9999");
      });
  }, [tasks, q]);

  const submit = async () => {
    try {
      if (editId) await api.patch(`/tasks/${editId}`, form);
      else await api.post("/tasks", form);
      toast.success(editId ? "Task updated" : "Task created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const changeStatus = async (id, status) => {
    await api.patch(`/tasks/${id}`, { status });
    load();
  };

  const exportCSV = () => {
    const rows = filtered.map((t) => ({
      title: t.title, function: t.function, priority: t.priority,
      status: t.status, due_date: t.due_date, owner: users.find((u) => u.id === t.owner_id)?.name,
    }));
    const blob = new Blob([toCSV(rows)], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "tasks.csv"; a.click();
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4" data-testid="tasks-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Work</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Tasks</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV}><Download className="h-4 w-4 mr-2" />CSV</Button>
          <Button onClick={() => { setEditId(null); setForm(empty); setOpen(true); }} data-testid="new-task-btn"><Plus className="h-4 w-4 mr-2" />New Task</Button>
        </div>
      </div>

      <Card className="p-4 bg-card border-border">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Input placeholder="Search tasks…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tasks-search" />
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{TASK_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.function || "all"} onValueChange={(v) => setFilters({ ...filters, function: v === "all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Function" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All functions</SelectItem>{FUNCTIONS.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.priority || "all"} onValueChange={(v) => setFilters({ ...filters, priority: v === "all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.owner_id || "all"} onValueChange={(v) => setFilters({ ...filters, owner_id: v === "all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All owners</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Function</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((t) => {
              const overdue = t.due_date && t.due_date < today && t.status !== "completed";
              return (
                <TableRow key={t.id} data-testid={`task-row-${t.id}`}
                          className={overdue ? "bg-red-500/5" : ""}>
                  <TableCell className="font-medium max-w-md truncate cursor-pointer"
                             onClick={() => { setEditId(t.id); setForm({ ...empty, ...t, subtasks: t.subtasks || [] }); setOpen(true); }}>
                    {t.title}
                  </TableCell>
                  <TableCell className="capitalize text-muted-foreground text-sm">{t.function}</TableCell>
                  <TableCell className="text-sm">{users.find((u) => u.id === t.owner_id)?.name || "—"}</TableCell>
                  <TableCell className={`font-mono text-xs ${overdue ? "text-red-400" : ""}`}>{t.due_date || "—"}</TableCell>
                  <TableCell><PriorityBadge value={t.priority} /></TableCell>
                  <TableCell>
                    <Select value={t.status} onValueChange={(v) => changeStatus(t.id, v)}>
                      <SelectTrigger className="h-8 w-[140px]" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <StatusBadge value={t.status} />
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No tasks match filters.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-card border-border">
          <DialogHeader><DialogTitle>{editId ? "Edit Task" : "New Task"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <TField label="Task Title" required>
              <Input placeholder="e.g. Finalise BOQ v3 for Malhotra" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" />
            </TField>
            <div className="grid grid-cols-2 gap-3">
              <TField label="Function" required>
                <Select value={form.function} onValueChange={(v) => setForm({ ...form, function: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FUNCTIONS.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
                </Select>
              </TField>
              <TField label="Priority" required>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </TField>
              <TField label="Owner" required>
                <Select value={form.owner_id || "none"} onValueChange={(v) => setForm({ ...form, owner_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Assignee" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </TField>
              <TField label="Co-Owner" hint="Optional additional assignee">
                <Select value={form.co_owner_id || "none"} onValueChange={(v) => setForm({ ...form, co_owner_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </TField>
              <TField label="Project" required>
                <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Link to project" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No project</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.client_name}</SelectItem>)}</SelectContent>
                </Select>
              </TField>
              <TField label="Status" required>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </TField>
              <TField label="Start Date" required>
                <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </TField>
              <TField label="End Date" required>
                <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </TField>
            </div>
            <TField label="Notes" hint="Optional">
              <Textarea placeholder="Context, links, blockers..." value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </TField>
            {editId && (
              <div className="pt-3 border-t border-border space-y-3">
                <TaskSubtasks taskId={editId} initial={form.subtasks || []} onChange={(list) => setForm({ ...form, subtasks: list })} />
                <Attachments kind="task" parentId={editId} />
                <Comments entity="task" parentId={editId} />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="task-save-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TField({ label, hint, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10.5px] text-muted-foreground/70 mt-1">{hint}</div>}
    </div>
  );
}

function TaskSubtasks({ taskId, initial, onChange }) {
  const [items, setItems] = useState(initial || []);
  const [text, setText] = useState("");
  useEffect(() => { setItems(initial || []); }, [initial]);

  const persist = async (next) => {
    setItems(next); onChange?.(next);
    try { await api.patch(`/tasks/${taskId}`, { subtasks: next }); } catch { toast.error("Failed to save subtasks"); }
  };
  const add = () => {
    if (!text.trim()) return;
    persist([...items, { id: crypto.randomUUID(), text: text.trim(), done: false }]);
    setText("");
  };
  const toggle = (id) => persist(items.map((i) => i.id === id ? { ...i, done: !i.done } : i));
  const remove = (id) => persist(items.filter((i) => i.id !== id));

  const done = items.filter((i) => i.done).length;
  return (
    <div className="space-y-2" data-testid={`subtasks-${taskId}`}>
      <div className="flex items-center gap-2 text-sm">
        <ListChecks className="h-4 w-4 text-primary" />
        <span className="font-semibold">Checklist</span>
        <span className="text-xs text-muted-foreground">{done} / {items.length} done</span>
      </div>
      {items.length > 0 && (
        <div className="space-y-1">
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-2 p-1.5 rounded border border-border bg-secondary/30">
              <input type="checkbox" checked={!!it.done} onChange={() => toggle(it.id)} data-testid={`subtask-check-${it.id}`} />
              <span className={`flex-1 text-sm ${it.done ? "line-through text-muted-foreground" : ""}`}>{it.text}</span>
              <button onClick={() => remove(it.id)} className="text-muted-foreground hover:text-red-400 text-xs">×</button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input placeholder="Add checklist item…" value={text} onChange={(e) => setText(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && add()} data-testid="subtask-input" />
        <Button size="sm" onClick={add} variant="outline">Add</Button>
      </div>
    </div>
  );
}
